import { useAtom } from "jotai";
import { type MouseEvent, useEffect, useRef } from "react";
import { canv } from "@/canv/global";
import { ContextMenuIcon } from "@/icons/widget-menu";
import { contextMenuVisibilityAtom } from "@/interface/AppStore";
import type { WidgetOptions } from "@/shared/types/widgetOptions";
import type { ActiveSubMenuState, SetActiveSubMenu } from "../../types";
import styles from "../../WidgetMenu.module.css";

interface UseContextMenuButtonProps {
    activeSubMenu: ActiveSubMenuState | null;
    setActiveSubMenu: SetActiveSubMenu;
}

export function useContextMenuButton({
    activeSubMenu,
    setActiveSubMenu,
}: UseContextMenuButtonProps) {
    const [contextMenuVisibility, setContextMenuVisibility] = useAtom(contextMenuVisibilityAtom);

    const prevActiveSubMenuNameRef = useRef<WidgetOptions | null>(activeSubMenu?.name ?? null);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        const button = event.currentTarget;

        setActiveSubMenu((prev) =>
            prev?.name === "contextMenu" ? null : { name: "contextMenu", anchor: button },
        );
        setContextMenuVisibility((prev) => {
            const nextIsVisible = !prev.isVisible;

            return {
                isVisible: nextIsVisible,
                anchor: nextIsVisible ? button : undefined,
            };
        });

        canv.contextMenu.buildSelectedContextMenuContent();
    };

    useEffect(() => {
        const prevName = prevActiveSubMenuNameRef.current;
        const nextName = activeSubMenu?.name ?? null;

        prevActiveSubMenuNameRef.current = nextName;

        if (prevName === nextName) {
            return;
        }

        if (contextMenuVisibility.isVisible && nextName !== "contextMenu") {
            setContextMenuVisibility({ isVisible: false, anchor: undefined });
        }
    }, [activeSubMenu, contextMenuVisibility.isVisible, setContextMenuVisibility]);

    useEffect(() => {
        if (!contextMenuVisibility.isVisible && activeSubMenu?.name === "contextMenu") {
            setActiveSubMenu(null);
        }
    }, [activeSubMenu, contextMenuVisibility.isVisible, setActiveSubMenu]);

    const isActive = contextMenuVisibility.isVisible;
    const content = <ContextMenuIcon className={isActive ? styles.svgFillActive : ""} />;

    return {
        content,
        handleClick,
        isActive,
    };
}
